import { Fields } from "@cd2/forms/build/es6"
import { IComponentProps } from "@cd2/tools/build/es6"
import React from "react"
import { IBranchSettings, useSettings } from "../../utils/useSettings"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"

export const defaultDeliveryMethodOptions = [
  {
    key: Math.random().toString(),
    value: "Collection From Store",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null as string | null,
  },
  {
    key: Math.random().toString(),
    value: "Same Day",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - By 10:30AM",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - By 12:00PM",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - Free Shipping",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - Next Day",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - Northern Ireland",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - Republic of Ireland",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - Saturday By 12:00PM",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "DPD - Single Item",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "Direct from supplier",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
  {
    key: Math.random().toString(),
    value: "Van",
    defaultIntercoPrice: 0,
    defaultNonIntercoPrice: null,
    deliveryType: null,
  },
]

export function useDeliveryOptions(opts?: { requireBranchId?: boolean; branchId?: string }) {
  const { branchId, requireBranchId } = opts ?? {}
  const skeem = window.skeem
  const { deliveryOptions } = useSettings()
  const [options, setOptions] = React.useState<typeof deliveryOptions | null>(null)
  useAsyncEffect(async () => {
    // require branch id and no branch id, set null options
    if (requireBranchId && !branchId) {
      setOptions(null)
      return
    }

    if (branchId) {
      const branch = await skeem.fetch2<IRecord<"branches"> & { settings: IBranchSettings }>({
        first: { name: "branches", attributes: ["settings"], matches: { id: branchId } },
      })
      setOptions(branch.settings?.deliveryOptions ?? [])
      return
    }

    setOptions(deliveryOptions)
  }, [JSON.stringify(deliveryOptions), branchId, requireBranchId])

  if (!options) {
    return []
  }

  if (options.length > 0) {
    return options.map(option => ({
      label: option.value,
      value: option.value,
      defaultIntercoPrice: option.defaultIntercoPrice,
      defaultNonIntercoPrice: option.defaultNonIntercoPrice,
    }))
  }

  return defaultDeliveryMethodOptions.map(option => ({
    label: option.value,
    value: option.value,
    defaultIntercoPrice: option.defaultIntercoPrice,
    defaultNonIntercoPrice: option.defaultNonIntercoPrice,
  }))
}

interface IDeliveryOption {
  label: string
  value: string
  defaultIntercoPrice?: number
  defaultNonIntercoPrice?: number | null
}

export function DeliveryMethodSelectList(
  props: Omit<
    IComponentProps<typeof Fields.Select2>,
    "options" | "getOptionLabel" | "getOptionValue" | "onChange" | "value"
  > & {
    onChange: (
      value: string,
      defaultIntercoPrice?: number,
      defaultNonIntercoPrice?: number | null,
    ) => void
    value?: string
    branchId?: string
    requireBranchId?: boolean
    required?: boolean
    error?: string
  },
) {
  const options = useDeliveryOptions(props)

  function setValue(opt: IDeliveryOption) {
    props.onChange(opt?.value, opt?.defaultIntercoPrice, opt?.defaultNonIntercoPrice)
  }

  return (
    <Fields.Select2
      label="Delivery Method"
      {...props}
      options={options}
      getOptionLabel={opt => opt?.label}
      getOptionValue={opt => opt?.value}
      onChange={setValue}
      value={options.find(opt => opt.value?.toLowerCase() === props.value?.toLowerCase())}
      showRequiredStar={props.required}
      error={props.error}
    />
  )
}

function getOption(opt?: (typeof defaultDeliveryMethodOptions)[number]["value"]) {
  return defaultDeliveryMethodOptions.find(v => v.value.toLowerCase() === opt?.toLowerCase())
}

function optionEq(opt?: (typeof defaultDeliveryMethodOptions)[number]["value"], value?: string) {
  return getOption(opt)?.value?.toLowerCase() === value?.toLowerCase()
}

DeliveryMethodSelectList.deliveryMethodOptions = defaultDeliveryMethodOptions
DeliveryMethodSelectList.getOption = getOption
DeliveryMethodSelectList.optionEq = optionEq
